import { useState } from 'react';
import { Link, useRouterState, useNavigate } from '@tanstack/react-router';
import { UserAvatarFilled, Menu } from '@carbon/icons-react';
import { useIsMobile } from '@/hooks/use-mobile';
import { usePwaInstall } from '@/hooks/use-pwa-install';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui';
import { OfflineIndicator, PwaInstallModal } from '@/components';
import { useAuth } from '../../hooks/use-auth';
import { ROLES } from '../../constants/roles.constants';

import './header-style.scss';

const Header = () => {
  const { user } = useAuth();
  const router = useRouterState();
  const navigate = useNavigate();
  const currentPath = router.location.pathname;
  const isMobile = useIsMobile();
  const [showPwaModal, setShowPwaModal] = useState(false);

  const isDriver = user?.role === ROLES.DRIVER;
  const pwa = usePwaInstall();

  // Show install option for drivers on mobile (not already installed)
  const showInstallOption = isDriver && pwa.showInstallPrompt;

  const handleInstallClick = () => {
    setShowPwaModal(true);
  };

  const NAV_ITEMS = {
    [ROLES.DRIVER]: [
      { label: 'Home', path: '/dashboard' },
      { label: 'Surveys', path: '/surveys' },
    ],
    default: [
      { label: 'Dashboard', path: '/dashboard' },
      { label: 'Organization', path: '/organizations' },
      { label: 'Surveys', path: '/surveys' },
    ],
  };

  const navItems =
    NAV_ITEMS[user?.role as keyof typeof NAV_ITEMS] ?? NAV_ITEMS.default;

  const handleProfileClick = () => {
    navigate({ to: '/profile' });
  };

  return (
    <header className="header" id="header">
      <div className="container-fluid">
        <div className="header-brand">
          <Link to={'/dashboard' as any} className="header-logo-link">
            <img src="/full-logo.svg" alt="Logo" className="header-logo" />
          </Link>
        </div>

        {/* Desktop: show offline indicator next to logo */}
        {/* Mobile: show offline indicator centered */}
        {isMobile ? (
          <div
            style={{
              position: 'absolute',
              left: '50%',
              transform: 'translateX(-50%)',
            }}
          >
            <OfflineIndicator />
          </div>
        ) : (
          <div className="ms-4">
            <OfflineIndicator />
          </div>
        )}

        <nav className="header-nav-center">
          {navItems.map((item) => (
            <div key={item.path} className="header-nav-item">
              <Link
                to={item.path as any}
                className={`header-nav-link ${
                  currentPath === item.path ? 'active' : ''
                }`}
                activeProps={{ className: 'active' }}
              >
                {item.label}
              </Link>
            </div>
          ))}
        </nav>

        <nav className="header-profile">
          <div
            className="header-profile-section"
            onClick={handleProfileClick}
            role="button"
            tabIndex={0}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') handleProfileClick();
            }}
          >
            <span className="header-profile-name">{user?.name}</span>
            <UserAvatarFilled className="large-icon brand-icon" />
          </div>
        </nav>

        {isMobile && (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button
                type="button"
                className="header-hamburger dropdown-menu-trigger"
                aria-label="Open menu"
              >
                <Menu className="large-icon brand-icon" />
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              {navItems.map((item) => (
                <DropdownMenuItem key={item.path} asChild>
                  <Link
                    to={item.path as any}
                    className={`dropdown-menu-item ${
                      currentPath === item.path ? 'active' : ''
                    }`}
                  >
                    {item.label}
                  </Link>
                </DropdownMenuItem>
              ))}
              <DropdownMenuItem asChild>
                <Link
                  to="/profile"
                  className={`dropdown-menu-item ${
                    currentPath === '/profile' ? 'active' : ''
                  }`}
                >
                  Profile
                </Link>
              </DropdownMenuItem>
              {showInstallOption && (
                <DropdownMenuItem
                  className="dropdown-menu-item"
                  onClick={handleInstallClick}
                >
                  Install app
                </DropdownMenuItem>
              )}
            </DropdownMenuContent>
          </DropdownMenu>
        )}

        {/* PWA Install Modal for drivers */}
        {isDriver && (
          <PwaInstallModal
            isOpen={showPwaModal}
            onClose={() => setShowPwaModal(false)}
            isIOS={pwa.isIOS}
            canInstall={pwa.canInstall}
            onInstall={pwa.triggerInstall}
          />
        )}
      </div>
    </header>
  );
};

export { Header };
